import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { Box, Button, Popover } from '@mui/material';
import { DateRange } from '@mui/icons-material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS, esES } from '@mui/x-date-pickers/locales';

interface RoomCalendarProps {
  identifier: 'checkIn' | 'checkOut';
  onChange: (value: Date) => void;
  value: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onAccept?: () => void;
}

export function HotelSearchCalendar({
  identifier,
  onChange,
  value,
  minDate,
  maxDate,
  open: controlledOpen,
  onOpen,
  onClose,
  onAccept,
}: RoomCalendarProps) {
  const { t, i18n } = useTranslation();
  const trigger = useRef<HTMLButtonElement>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const label = t(`hotels.searcher.${identifier}`);
  const locale = i18n.language?.startsWith('es') ? 'es' : 'en';
  const restoreFocus = useRef(true);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open && restoreFocus.current) trigger.current?.focus();
    wasOpen.current = open;
  }, [open]);
  const close = (restore = true) => {
    restoreFocus.current = restore;
    setLocalOpen(false);
    onClose?.();
  };
  const select = (date: Dayjs | null) => {
    if (
      !date?.isValid() ||
      date.isBefore(dayjs().startOf('day')) ||
      (minDate && date.isBefore(dayjs(minDate), 'day')) ||
      (maxDate && date.isAfter(dayjs(maxDate), 'day'))
    )
      return;
    onChange(date.toDate());
    close(!onAccept);
    onAccept?.();
  };
  return (
    <Box sx={{ width: '100%', minWidth: { md: 100 }, maxWidth: { md: 200 } }}>
      <Button
        ref={trigger}
        fullWidth
        variant="outlined"
        startIcon={<DateRange />}
        aria-label={`${label}${value ? `: ${dayjs(value).format('DD-MM-YYYY')}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setLocalOpen(true);
          onOpen?.();
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setLocalOpen(true);
            onOpen?.();
          }
        }}
        sx={{
          justifyContent: 'flex-start',
          minHeight: 40,
          textTransform: 'none',
          borderColor: 'divider',
          '&.Mui-focusVisible, &:focus-visible': {
            outline: '3px solid',
            outlineColor: 'primary.main',
            outlineOffset: 3,
            bgcolor: 'action.focus',
          },
          color: value ? 'text.primary' : 'text.secondary',
          '& .MuiButton-startIcon': { color: 'primary.main' },
        }}
      >
        {value ? dayjs(value).format('DD-MM-YYYY') : label}
      </Button>
      <Popover
        open={open}
        anchorEl={trigger.current}
        onClose={() => close()}
        transitionDuration={0}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ role: 'dialog', 'aria-label': label, sx: { bgcolor: 'background.paper' } }}
      >
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={locale}
          localeText={(locale === 'es' ? esES : enUS).components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <StaticDatePicker
            autoFocus
            disablePast
            displayStaticWrapperAs="desktop"
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            value={value ? dayjs(value) : null}
            onChange={select}
            slotProps={{ toolbar: { hidden: true }, actionBar: { actions: [] } }}
          />
        </LocalizationProvider>
      </Popover>
    </Box>
  );
}
